import {useState, useEffect, useRef} from 'react'
import {useCustomQuery, useCustomMutation, useAccessToken} from '@salesforce/commerce-sdk-react'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import mockData from '../../../components/pdp-reviews/mockData';
import useIsMobile from './use-is-mobile';
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useBrand} from '../../../contexts/brand-context'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../../utils/site-config'

const loadMockedData = false

/**
 * Hook to fetch address suggestions based on address and postal code.
 */
const useBazarVoice = ({
    pid,
    page = 0,
    sort = 'SubmissionTime:desc',
    withPhotos = false,
    limitOverride = null
}) => {
    const {app: appConfig} = getConfig()
    const {brandId = 'garage'} = useBrand()
    const isMobile = useIsMobile();
    const [isLoading, setIsLoading] = useState(true)
    const [reviewsError, setReviewsError] = useState(null)
    const [limit, setLimit] = useState(limitOverride)
    const {site} = useMultiSite()
    const siteId = site?.id

    const BV_ENABLE_RR_C2022 = getSiteEnvVarValue(appConfig, 'BV_ENABLE_RR_C2022', siteId) == 'true'

    useEffect(() => {
        if (limitOverride !== null) {
            setLimit(limitOverride)
            return
        }
        setLimit(4)
    }, [isMobile, limitOverride]);

    const {data: reviews} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    c_pid: pid,
                    c_brandId: brandId,
                    c_page: page,
                    c_sort: sort || 'SubmissionTime:desc',
                    ...(limit !== null && { c_limit: limit }),
                    ...(withPhotos && { c_withPhotos: true })
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/bv-review',
                    apiName: 'bazaarvoice'
                }
            },
            rawResponse: false
        },
        {
            enabled: BV_ENABLE_RR_C2022 && limit !== null,
            onSuccess: (result) => {
                setIsLoading(false)
                return result
            },
            onError: () => {
                setIsLoading(false)
                setReviewsError('Error fetching reviews')
            }
        }
    )

    return {
        isLoading: loadMockedData ? false : isLoading,
        reviews: loadMockedData ? mockData.bvReviewsAPIResponse : reviews,
        reviewsError
    }
}

/**
 * Fetches ALL photo reviews across all pages and returns the accumulated list.
 * Works around BV API per-page limits by paginating internally.
 */
const useAllPhotoReviews = ({pid}) => {
    const [page, setPage] = useState(0)
    const [accumulated, setAccumulated] = useState([])
    const [isComplete, setIsComplete] = useState(false)
    const processedPagesRef = useRef(new Set())

    // Reset when pid changes
    useEffect(() => {
        setPage(0)
        setAccumulated([])
        setIsComplete(false)
        processedPagesRef.current = new Set()
    }, [pid])

    const {reviews} = useBazarVoice({
        pid,
        page,
        withPhotos: true,
        limitOverride: 100
    })

    useEffect(() => {
        if (!reviews?.Results) return

        // Key on the API's own Offset field so stale React Query data for a
        // previous page never gets mistakenly processed under a new page number.
        const responseOffset = reviews.Offset ?? 0
        if (processedPagesRef.current.has(responseOffset)) return
        processedPagesRef.current.add(responseOffset)

        setAccumulated((prev) => [...prev, ...reviews.Results])

        const totalFetched = responseOffset + reviews.Results.length
        if (totalFetched < (reviews.TotalResults ?? 0)) {
            setPage((p) => p + 1)
        } else {
            setIsComplete(true)
        }
    }, [reviews])

    return {
        allPhotoReviews: accumulated,
        isLoadingAllPhotos: !isComplete
    }
}

const useReviewForm = ({pid}) => {
    const [isLoading, setIsLoading] = useState(true)
    const {brandId = 'garage'} = useBrand()

    const {refetch} = useCustomQuery({
        options: {
            method: 'GET',
            parameters: {
                c_pid: pid,
                c_brandId: brandId
            },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/bv-reviewform',
                apiName: 'bazaarvoice'
            }
        },
        rawResponse: false
    },
    {
        enabled: false
    })

    const getReviewForm = async ({pid, shouldFetch = true}) => {
        if (!shouldFetch) return
        setIsLoading(true)

        try {
            return await refetch({
                options: {
                    parameters: {
                        c_pid: pid
                    }
                }
            })
        } catch (error) {
            logger.error('Error get review form', {
                namespace: 'bazaarvoice',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
    }

    return {
        isLoading,
        getReviewForm
    }
}

const review = () => {
    const {brandId = 'garage'} = useBrand()
    const [selectedId, setSelectedId] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const [terms, setTerms] = useState(false)
    const [rating, setRating] = useState('')
    const [title, setTitle] = useState('')
    const [userReview, setUserReview] = useState('')
    const [username, setUsername] = useState('')
    const [hdif, setHDIF] = useState('')
    const [email, setEmail] = useState('')
    const [height, setHeight] = useState('')
    const [weight, setWeight] = useState('')
    const [size, setSize] = useState('')
    const [photos, setPhotos] = useState([])

    const mutation = useCustomMutation({
        options: {
            method: 'POST',
            body: JSON.stringify({
                termsAndConditions: terms,
                rating: rating,
                title: title,
                review: userReview,
                username: username,
                howDoesItFit: hdif,
                userEmail: email,
                height: height,
                weight: weight,
                size: size,
                photoUrls: photos
            }),
            parameters: {
                c_pid: selectedId,
                c_brandId: brandId
            },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/bv-submitreview',
                apiName: 'bazaarvoice'
            }
        }
    })

    const submitReview = async ({pid, formData, shouldFetch = true}) => {
        if (!shouldFetch) return
        setSelectedId(pid)
        let data

        let form = JSON.parse(formData)

        setTerms(form.agreedtotermsandconditions)
        setRating(parseInt(form.rating))
        setTitle(form.title)
        setUserReview(form.reviewtext)
        setUsername(form.usernickname)
        setHDIF(parseInt(form.rating_HowDoesItFit))
        setEmail(form.useremail)
        setHeight(form.contextdatavalue_height)
        setWeight(form.contextdatavalue_weight)
        setSize(form.additionalfield_sizeOrdered)

        let photos = []

        if (form.photourl_1) {
            photos.push(form.photourl_1)
        }

        if (form.photourl_2) {
            photos.push(form.photourl_2)
        }

        if (form.photourl_3) {
            photos.push(form.photourl_3)
        }

        if (form.photourl_4) {
            photos.push(form.photourl_4)
        }

        if (form.photourl_5) {
            photos.push(form.photourl_5)
        }

        if (form.photourl_6) {
            photos.push(form.photourl_6)
        }

        setPhotos(photos)

        setIsLoading(true)
        try {
            data = await mutation.mutateAsync({
                method: 'POST',
                options: {
                    parameters: {
                        c_pid: selectedId,
                        c_brandId: brandId
                    },
                    body: JSON.stringify({
                        termsAndConditions: terms,
                        rating: rating,
                        title: title,
                        review: userReview,
                        username: username,
                        howDoesItFit: hdif,
                        userEmail: email,
                        height: height,
                        weight: weight,
                        size: size,
                        photoUrls: photos
                    })
                }
            })
        } catch (error) {
            logger.error('Error submitting review', {
                namespace: 'bazaarvoice',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
        return data
    }

    return {
        isLoading,
        submitReview
    }
}

const reviewFeedback = () => {
    const {brandId = 'garage'} = useBrand()
    const [selectedFeedback, setSelectedFeedback] = useState('')
    const [selectedId, setSelectedId] = useState('');
    const [isLoading, setIsLoading] = useState(false)

    const mutation = useCustomMutation({
        options: {
            method: 'POST',
            parameters: {
                c_feedback: selectedFeedback,
                c_contentId: selectedId,
                c_brandId: brandId
            },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/bv-submitfeedback',
                apiName: 'bazaarvoice'
            }
        }
    })

    const submitFeedback = async ({Id, feedback, shouldFetch = true}) => {
        if (!shouldFetch) return
        setSelectedFeedback(feedback)
        setSelectedId(Id);
        let data

        setIsLoading(true)
        try {
            data = await mutation.mutateAsync({
                method: 'POST',
                options: {
                    parameters: {
                        c_feedback: selectedFeedback,
                        c_contentId: selectedId,
                        c_brandId: brandId
                    }
                }
            })
        } catch (error) {
            logger.error('Error submitted review feedback', {
                namespace: 'bazaarvoice',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
        return data
    }

    return {
        isLoading,
        submitFeedback
    }
}

const reviewTranslation = () => {
    const [isLoading, setIsLoading] = useState(false)
    const bodyRef = useRef({})
    const {getTokenWhenReady} = useAccessToken()

    const mutation = useCustomMutation({
        options: {
            method: 'POST',
            get body() { return JSON.stringify(bodyRef.current) },
            customApiPathParameters: {
                apiVersion: 'v1',
                endpointPath: '/translate-review',
                apiName: 'google'
            }
        }
    })

    const translateReview = async ({reviewText, targetLocale, shouldFetch = true}) => {
        if (!shouldFetch) return
        let data
        bodyRef.current = {reviewText, targetLocale}
        setIsLoading(true)
        try {
            const token = await getTokenWhenReady()
            data = await mutation.mutateAsync({
                options: {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                }
            })
        } catch (error) {
            logger.error('Error translating review', {
                namespace: 'bazaarvoice',
                additionalProperties: {error: error.message, stack: error.stack}
            })
        } finally {
            setIsLoading(false)
        }
        return data
    }

    return {
        isLoading,
        translateReview
    }
}

const useReviewPhotos = (reviews = []) => {
    return reviews.reduce((acc, review) => {
        const reviewPhotos = review?.Photos || []
        if (reviewPhotos.length > 0) {
            const photosWithReview = reviewPhotos
                .map((photo, index) => ({
                    key: `${review?.Id || 'review'}-${photo?.Id || index}`,
                    url: photo?.Sizes?.normal?.Url || photo?.Sizes?.thumbnail?.Url,
                    review,
                    index
                }))
                .filter((photo) => Boolean(photo.url))
            return [...acc, ...photosWithReview]
        }
        return acc
    }, [])
}

/**
 * Hook to fetch AI-generated review summary for a product.
 */
const useReviewSummary = ({pid}) => {
    const {app: appConfig} = getConfig()
    const {brandId = 'garage'} = useBrand()
    const {site, locale} = useMultiSite()
    const [isLoading, setIsLoading] = useState(true)
    const siteId = site?.id

    const BV_ENABLE_RR_C2022 = getSiteEnvVarValue(appConfig, 'BV_ENABLE_RR_C2022', siteId) == 'true'

    const {data} = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters: {
                    c_pid: pid,
                    c_brandId: brandId,
                    siteId: site?.id,
                    locale: locale?.id
                },
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath: '/bv-reviewsummary',
                    apiName: 'bazaarvoice'
                }
            },
            rawResponse: false
        },
        {
            enabled: BV_ENABLE_RR_C2022 && !!pid,
            onSuccess: () => {
                setIsLoading(false)
            },
            onError: () => {
                setIsLoading(false)
            }
        }
    )

    return {
        isLoading,
        summary: data?.success ? data.summary : null
    }
}

export {useBazarVoice, useAllPhotoReviews, useReviewPhotos, review, reviewFeedback, reviewTranslation, useReviewForm, useReviewSummary}
